import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { sortRows, type SortDirection, type SortState } from '../lib/listSort';

/**
 * The ruled list every admin table shares.
 *
 * All seven of them were already the same table: `.ledger` inside an
 * `overflow-x-auto`, a header row built by mapping a string array, and a body
 * of hand written cells. The convention for "this column holds the actions" was
 * that its header string happened to read `Actions`, `Edit` or `Remove`, so
 * renaming a column silently unhid its header. Six copies of that.
 *
 * A column is declared once here and is its own id, header, cell, sort key and
 * searchable text. The failure that replaces is two arrays paired by position,
 * where inserting a heading and forgetting the cell shifts every later label one
 * column right of its data, which renders perfectly and is wrong.
 *
 * ## Still a real table
 *
 * A grid of divs with ARIA roles would allow resizable columns, which this does
 * not need. A real `<table>` brings `aria-sort`, `scope`, row and column
 * navigation in a screen reader's table mode, and the register's own tabular
 * numerals, none of which would have to be rebuilt.
 *
 * ## Nesting
 *
 * Groups are `<tbody>` elements, which is what makes them structural rather
 * than decorative, and is also what makes the ruling come out right:
 * `.ledger tbody tr + tr` only matches within one body, so the first row of a
 * group gets no hairline and the group's own heading rule separates it.
 *
 * Sorting orders rows WITHIN a group and never reorders the groups. Group order
 * is taken from the order the rows arrived in, which is the server's, so the
 * platform accounts that `superadmin.ts` deliberately sorts before every
 * organization stay in front no matter which column someone clicks.
 */

export interface LedgerColumn<Row> {
  /** React key, sort parameter value, and part of the detail row's id. */
  id: string;
  /**
   * Header content. A node rather than a string because the report names its
   * first column after whatever it is currently grouped by.
   */
  header: ReactNode;
  /** Announced but not shown. The actions column. */
  headerHidden?: boolean;
  cell: (row: Row, ctx: RowContext) => ReactNode;
  align?: 'right';
  /** Draws `.rule-y` on the leading edge, to fence a run of figures. */
  ruled?: boolean;
  /** Its presence makes the column sortable. Null always sorts last. */
  sort?: (row: Row) => string | number | null;
  /** Opens descending: counts, rates and dates are asked "most" first. */
  numeric?: boolean;
  /** Matched by the search box. */
  search?: (row: Row) => string | null | undefined;
  /** Applied to the header cell and every body cell. */
  className?: string;
  /** This column's cell in the totals row. Only the report has one. */
  foot?: ReactNode;
}

export interface RowContext {
  /** True when this row's detail is open. */
  expanded: boolean;
  /** Open this row, closing whichever was open. Closes it when already open. */
  toggle: () => void;
  /**
   * The detail row's id, for `aria-controls`. Null while closed, because the
   * detail is not rendered then and pointing at nothing is worse than not
   * pointing.
   */
  detailId: string | null;
}

export interface LedgerGroup<Row> {
  /** This row's group id. */
  of: (row: Row) => string;
  /** The group's heading. Gets the id and the group's own rows for a count. */
  label: (id: string, rows: readonly Row[]) => ReactNode;
  /**
   * The order to show the groups in. Omit it when the server already sorts by
   * the thing being grouped on, which is worth doing: the platform people list
   * is ordered platform accounts first and then by organization, and the
   * closures list by date, so taking the arrival order means neither of them
   * carries a second copy of a rule that lives in SQL.
   *
   * Declare it when the grouping runs across the server's ordering instead. The
   * roll arrives sorted by surname, so whether "Left" or "On roll" came first
   * would depend on whose name happened to sort earliest, which is not an order
   * at all. Ids left out keep their arrival order, after the listed ones.
   */
  order?: readonly string[];
}

/** Narrows rows to those matching `query` on any column that declares `search`. */
export function searchRows<Row>(
  rows: readonly Row[],
  columns: readonly LedgerColumn<Row>[],
  query: string,
): Row[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...rows];
  const searchable = columns.filter((column) => column.search);
  return rows.filter((row) =>
    searchable.some((column) => column.search?.(row)?.toLowerCase().includes(needle)),
  );
}

export function Ledger<Row>({
  rows,
  columns,
  rowId,
  caption,
  sort,
  onSort,
  group,
  collapsed,
  onCollapse,
  detail,
  expanded = null,
  onExpand,
  foot,
  empty,
  noMatch,
  total,
}: {
  /** Already narrowed by whatever the page filters on. Sorting happens here. */
  rows: readonly Row[];
  columns: readonly LedgerColumn<Row>[];
  rowId: (row: Row) => string;
  /** Announced table name, rendered as a visually hidden caption. */
  caption: string;
  sort: SortState | null;
  onSort: (id: string) => void;
  /** Omit for a flat list. */
  group?: LedgerGroup<Row>;
  /** Collapsed group ids. Owned by the page. */
  collapsed?: ReadonlySet<string>;
  onCollapse?: (id: string, collapsed: boolean) => void;
  /**
   * Supplying this lets rows open. Rendered in a full width cell beneath the
   * row. `close` is handed over so a caller can render its detail from a plain
   * module scope function instead of reaching back for its own state.
   */
  detail?: (row: Row, close: () => void) => ReactNode;
  /** Controlled and single open, matching the `editing` state it replaces. */
  expanded?: string | null;
  onExpand?: (id: string | null) => void;
  /** Render a totals row from each column's `foot`. */
  foot?: boolean;
  /** No rows at all. */
  empty: ReactNode;
  /** Rows exist but none survived the filter. */
  noMatch?: ReactNode;
  /** Rows before filtering, so the two empty cases can be told apart. */
  total?: number;
}) {
  const base = useId();
  const table = useRef<HTMLTableElement>(null);
  const detailCell = useRef<HTMLTableCellElement>(null);

  const ordered = useMemo(() => {
    if (!sort) return [...rows];
    const column = columns.find((c) => c.id === sort.id);
    const value = column?.sort;
    if (!value) return [...rows];
    return sortRows(rows, value, sort.direction);
  }, [rows, columns, sort]);

  /*
   * Group order is either declared by the caller or, failing that, taken from
   * the rows AS RECEIVED, before the sort above. Two of these lists arrive
   * ordered by the very dimension they group on, so taking the arrival order
   * there avoids a second copy of a rule that lives in SQL. Either way the
   * order does not come from the SORTED rows, which is what stops a click on
   * one column scattering the groups.
   */
  const groups = useMemo(() => {
    if (!group) return null;
    const seen: string[] = [];
    for (const row of rows) {
      const id = group.of(row);
      if (!seen.includes(id)) seen.push(id);
    }
    const declared = group.order ?? [];
    const order = [
      ...declared.filter((id) => seen.includes(id)),
      ...seen.filter((id) => !declared.includes(id)),
    ];
    return order.map((id) => ({
      id,
      rows: ordered.filter((row) => group.of(row) === id),
    }));
  }, [group, rows, ordered]);

  /*
   * Focus follows the disclosure. Opening moves into the detail, and closing
   * puts focus back on the control that opened it. Without the second half, a
   * save that closes the form from its own onSuccess unmounts the focused
   * element and drops the keyboard at the top of the document.
   */
  const previous = useRef<string | null>(null);
  useEffect(() => {
    const before = previous.current;
    previous.current = expanded;
    if (expanded) {
      detailCell.current?.focus();
    } else if (before) {
      table.current
        ?.querySelector<HTMLElement>(`[data-row="${CSS.escape(before)}"] [aria-expanded]`)
        ?.focus();
    }
  }, [expanded]);

  if (rows.length === 0) {
    return <>{total !== undefined && total > 0 && noMatch ? noMatch : empty}</>;
  }

  const span = columns.length;
  const captionId = `${base}-caption`;

  /*
   * One shape for a body cell and a totals cell: the column's label, then its
   * content. The label is what the cards layout shows beside each value once
   * the header row is no longer above the column; here it is display:none.
   *
   * aria-hidden, because the column headers stay in the accessibility tree in
   * both layouts, so the label is a visual duplicate and must not become part
   * of any row's or cell's name. The first column names the row and the
   * hidden-header column holds the actions, and neither wants a label reading
   * "Name" or "Actions" over it; data-cell tells the cards CSS which is which.
   *
   * The stylesheet also reads the title cell for a link. A row whose title
   * links somewhere IS that link: the whole row is the click target, in the
   * table and as a card, so there is no Manage button. The table keeps the
   * row's other buttons, lifted above the link; the card shows none, so every
   * one of them must be on the page the title opens. A list whose rows need a
   * button on a phone (Holidays, Organizations, People) leaves its title as
   * text.
   *
   * The value is wrapped in a div so that a cell with several children (a date
   * over its weekday, a row of buttons) is one item in the card's flow.
   */
  const cells = (content: (column: LedgerColumn<Row>) => ReactNode, pad?: string) =>
    columns.map((column, index) => {
      const title = index === 0;
      const actions = column.headerHidden === true;
      return (
        <td
          key={column.id}
          role="cell"
          className={cell(column, pad)}
          data-cell={title ? 'title' : actions ? 'actions' : undefined}
        >
          {title || actions ? null : (
            <span className="ledger-label" aria-hidden="true">
              {column.header}
            </span>
          )}
          <div className="ledger-value">{content(column)}</div>
        </td>
      );
    });

  const body = (list: readonly Row[]) =>
    list.map((row) => {
      const id = rowId(row);
      const open = expanded === id;
      const detailId = `${base}-detail-${id}`;
      const ctx: RowContext = {
        expanded: open,
        toggle: () => onExpand?.(open ? null : id),
        detailId: open ? detailId : null,
      };
      return (
        <Fragment key={id}>
          <tr role="row" data-row={id} className="hover:bg-paper-sunk">
            {cells((column) => column.cell(row, ctx))}
          </tr>
          {open && detail ? (
            <tr role="row" data-detail id={detailId}>
              {/*
                tabIndex so focus has somewhere to land when the detail opens.
                It holds a form, and announcing the row's own heading before it
                is more use than dropping the reader on the first input.
              */}
              <td
                ref={detailCell}
                role="cell"
                tabIndex={-1}
                colSpan={span}
                className="bg-paper-sunk px-4 py-4"
              >
                {detail(row, () => onExpand?.(null))}
              </td>
            </tr>
          ) : null}
        </Fragment>
      );
    });

  /*
   * Two boxes around the table, and roles on every part of it.
   *
   * The outer box is a container query container (see the cards block at the
   * end of styles.css): narrower than 40rem, the table is laid out as cards
   * and needs no scrolling. Making it the container also gives it inline-size
   * containment, which is what keeps a wide table from widening the page: the
   * Manage pages stack their sections in a grid, and an auto grid track grows
   * to its items' min-content width, which for a section is its table. With
   * containment the box's intrinsic width is empty, it fills whatever it is
   * given, and the table scrolls inside it.
   *
   * The inner box is the scroller. It is separate because a container query
   * cannot style the container itself, and in cards mode this box has to stop
   * clipping: a focus outline at a card's edge is drawn outside the element
   * and an overflow box would cut it off. It is `relative` so that it is the
   * containing block for the sr-only caption and hidden column headers. They
   * are absolutely positioned, and an overflow box only clips what it
   * contains; without this the hidden Actions header sits at the table's
   * right edge, outside the viewport on a phone, and widens the page by that
   * much without painting anything.
   *
   * The explicit roles say what the markup already means. Browsers drop a
   * table's implicit semantics once its parts are displayed as anything but
   * table-*, which is exactly what the cards do; with the roles written down,
   * a screen reader's table mode and every `getByRole('row')` keep working in
   * both layouts. aria-labelledby names the table by its caption for the same
   * reason.
   */
  return (
    <div className="@container/ledger">
      <div className="ledger-scroller relative overflow-x-auto">
        <table ref={table} role="table" aria-labelledby={captionId} className="ledger text-sm">
          <caption id={captionId} className="sr-only">
            {caption}
          </caption>
          <thead role="rowgroup">
            <tr role="row">
              {columns.map((column) => (
                <Header key={column.id} column={column} sort={sort} onSort={onSort} />
              ))}
            </tr>
          </thead>

          {groups ? (
            groups.map(({ id, rows: within }) => {
              const shut = collapsed?.has(id) ?? false;
              const groupId = `${base}-group-${id}`;
              return (
                <tbody key={id} role="rowgroup" id={groupId}>
                  <tr role="row" data-group>
                    {/*
                      A group heading labels the rows under it rather than
                      naming any column, so it spans the table and is scoped to
                      its row group. `colgroup` would claim it heads a column.
                    */}
                    <th
                      role="rowheader"
                      colSpan={span}
                      scope="rowgroup"
                      className="border-t border-rule-strong bg-paper-sunk px-4 py-2 text-left"
                    >
                      <button
                        type="button"
                        className="flex items-center gap-2 text-sm font-semibold print:hidden"
                        aria-expanded={!shut}
                        aria-controls={groupId}
                        onClick={() => onCollapse?.(id, !shut)}
                      >
                        <Chevron open={!shut} />
                        {group?.label(id, within)}
                      </button>
                      {/* Printed pages get the label without the control. */}
                      <span className="hidden items-center gap-2 text-sm font-semibold print:inline-flex">
                        {group?.label(id, within)}
                      </span>
                    </th>
                  </tr>
                  {shut ? null : body(within)}
                </tbody>
              );
            })
          ) : (
            <tbody role="rowgroup">{body(ordered)}</tbody>
          )}

          {foot ? (
            <tfoot role="rowgroup">
              <tr role="row" data-foot className="border-t-2 border-rule-strong">
                {cells((column) => column.foot, 'py-3')}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </div>
  );
}

function cell<Row>(column: LedgerColumn<Row>, pad = 'py-2.5'): string {
  return [
    column.ruled ? 'rule-y px-3' : 'px-4',
    pad,
    column.align === 'right' ? 'text-right' : '',
    column.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

function Header<Row>({
  column,
  sort,
  onSort,
}: {
  column: LedgerColumn<Row>;
  sort: SortState | null;
  onSort: (id: string) => void;
}) {
  const active = sort?.id === column.id ? sort.direction : null;
  /*
   * Eyebrow lettering, but ink-soft rather than the eyebrow's own ink-faint,
   * which measures 2.75:1 against the sheet in the light theme where AA asks
   * 4.5:1 for text. A column heading names what is under it and half of them
   * are now buttons, so this is neither decoration nor an ordinary label. The
   * eyebrows elsewhere in the app keep their own colour.
   */
  const label = (
    <span className={column.headerHidden ? 'sr-only' : 'eyebrow text-ink-soft'}>
      {column.header}
    </span>
  );

  const className = [
    column.ruled ? 'rule-y px-3' : 'px-4',
    'py-3',
    column.align === 'right' ? 'text-right' : 'text-left',
    column.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  if (!column.sort) {
    return (
      <th role="columnheader" scope="col" className={className}>
        {label}
      </th>
    );
  }

  return (
    /*
     * `aria-sort` on the cell and the control inside it, which is the shape a
     * real table is expected to have. The button keeps the column's own name:
     * the state is already on `aria-sort`, and an "sort by name, ascending"
     * label would replace the visible one and then drift from it.
     */
    <th
      role="columnheader"
      scope="col"
      aria-sort={active ? (active === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={className}
    >
      <button
        type="button"
        // Padding a finger can land on, taken back out of the header's own so
        // the row does not grow. The cards block resets it in the sort strip.
        className={`inline-flex items-center gap-1.5 pointer-coarse:-my-3 pointer-coarse:py-3 ${column.align === 'right' ? 'flex-row-reverse' : ''}`}
        onClick={() => onSort(column.id)}
      >
        {label}
        <SortMark direction={active} />
      </button>
    </th>
  );
}

/**
 * Sortability drawn rather than tinted, so it survives a monochrome print for
 * the same reason a mark is a letterform: the solid triangle is the state, and
 * the colour only says whether this column is the sorted one.
 */
function SortMark({ direction }: { direction: SortDirection | null }) {
  return (
    <svg
      width="7"
      height="10"
      viewBox="0 0 7 10"
      aria-hidden="true"
      focusable="false"
      className={direction ? 'text-ink' : 'text-ink-faint'}
    >
      <path
        d="M3.5 0 L7 3.5 L0 3.5 Z"
        fill="currentColor"
        opacity={direction === 'desc' ? 0.2 : 1}
      />
      <path
        d="M3.5 10 L0 6.5 L7 6.5 Z"
        fill="currentColor"
        opacity={direction === 'asc' ? 0.2 : 1}
      />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 10 10"
      aria-hidden="true"
      focusable="false"
      className="text-ink-soft"
      style={{ transform: open ? 'rotate(90deg)' : undefined }}
    >
      <path
        d="M3 1 L7 5 L3 9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The controls above a ruled list.
 *
 * Narrowing on the left, searching on the right. No sort control: the column
 * headings are the sorting bar, they are where a reader already looks for it,
 * and a second one would be two ways to do a single thing.
 *
 * Not a `.panel`. A panel is the sunk surface for something you write into, and
 * a strip of filters over a list you are reading is not that. It carries no rule
 * of its own either, because the table's own heading rule is forty pixels below
 * and two heavy rules that close together read as a box.
 */
export interface LedgerFacet {
  /** Announced name for the group of buttons, e.g. "Filter by role". */
  label: string;
  /** The empty string is "all", which is the absence of a filter. */
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string; count: number }[];
}

/**
 * The chips every list shares: where a record is in its life.
 *
 * A period retires, a class retires, a student leaves, an account is
 * deactivated. Every one of those rows stays forever so the attendance history
 * keeps adding up, and left in the default view they accumulate until the list
 * gets worse every year. So the tabs are exclusive: each shows one kind, and
 * the counts are what tell a reader there is anything elsewhere at all.
 *
 * A retired record can then be deleted, which hides it from the organization
 * entirely. Only a platform administrator can see or undo that, so the third
 * chip is absent for everybody else rather than present and empty: a count of
 * zero would be a claim, and it is not one this list is allowed to make.
 */
/** Where a record is in its life. Deleted outranks inactive: it came after it. */
export type Stage = 'live' | 'inactive' | 'deleted';

export function stageOf(row: { isActive: boolean; deletedAt?: string | null }): Stage {
  if (row.deletedAt) return 'deleted';
  return row.isActive ? 'live' : 'inactive';
}

/**
 * The rows belonging on the tab that is showing.
 *
 * Anything unrecognised in the address resolves to the live ones, matching the
 * rest of this app: a search parameter is whatever somebody typed, and the
 * default is the honest answer to a value nobody defined.
 */
export function onStage<Row extends { isActive: boolean; deletedAt?: string | null }>(
  rows: readonly Row[],
  show: string,
): Row[] {
  const want: Stage = show === 'deleted' ? 'deleted' : show === 'inactive' ? 'inactive' : 'live';
  return rows.filter((row) => stageOf(row) === want);
}

export function statusFacet({
  label,
  liveLabel,
  inactiveLabel,
  value,
  onChange,
  live,
  inactive,
  deleted,
}: {
  /** Announced name for the group, e.g. "Filter periods by status". */
  label: string;
  /** The default chip: "Active", "On roll". */
  liveLabel: string;
  /** The second chip: "Retired", "Left", "Deactivated". */
  inactiveLabel: string;
  value: string;
  onChange: (value: string) => void;
  live: number;
  inactive: number;
  /** Omit entirely for anybody who may not see deleted records. */
  deleted?: number;
}): LedgerFacet {
  return {
    label,
    value,
    onChange,
    options: [
      { value: '', label: liveLabel, count: live },
      { value: 'inactive', label: inactiveLabel, count: inactive },
      ...(deleted === undefined ? [] : [{ value: 'deleted', label: 'Deleted', count: deleted }]),
    ],
  };
}

export function LedgerToolbar({
  facets,
  query,
  onQuery,
  searchLabel = 'Search',
  searchPlaceholder,
  shown,
  total,
  children,
}: {
  facets?: readonly LedgerFacet[];
  /** Omit both to render no search box. */
  query?: string;
  onQuery?: (value: string) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  /** For the count. Omit to render none. */
  shown?: number;
  total?: number;
  /** Controls the page owns: a date range, an organization picker. */
  children?: ReactNode;
}) {
  const narrowed = shown !== undefined && total !== undefined && shown !== total;
  const summary = narrowed ? `${shown} of ${total} shown` : `${total ?? 0} shown`;

  /*
   * Shown at once and announced late, as the picker does. One string doing both
   * jobs gives either a counter that visibly lags the typing or a screen reader
   * interrupting itself on every keystroke.
   */
  const [announced, setAnnounced] = useState(summary);
  useEffect(() => {
    const timer = window.setTimeout(() => setAnnounced(summary), 300);
    return () => window.clearTimeout(timer);
  }, [summary]);

  return (
    <div className="mb-4 print:hidden">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {facets?.map((facet) => (
          <div key={facet.label} role="group" aria-label={facet.label} className="flex gap-1">
            {facet.options.map((option) => {
              const on = facet.value === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => facet.onChange(option.value)}
                  /*
                   * The header nav's selection idiom: the sunk tone marks the
                   * choice. Not a bordered pill, and not a colour, because a
                   * colour would be the only carrier of the state.
                   */
                  className="flex items-center gap-1.5 rounded px-2.5 py-1.5 text-sm transition-colors pointer-coarse:min-h-11"
                  style={{
                    background: on ? 'var(--color-paper-sunk)' : 'transparent',
                    color: on ? 'var(--color-ink)' : 'var(--color-ink-soft)',
                    fontWeight: on ? 600 : 400,
                  }}
                >
                  {option.label}
                  <span className="font-mono text-xs tabular">{option.count}</span>
                </button>
              );
            })}
          </div>
        ))}

        {children}

        {onQuery ? (
          /*
            The whole row on a phone, at the right on a desktop. A fixed 224px
            box pushed to the right of a 330px row is neither.
          */
          <label className="grid w-full gap-1.5 sm:ml-auto sm:w-auto">
            <span className="eyebrow">{searchLabel}</span>
            <input
              type="search"
              className="field w-full sm:w-56"
              value={query ?? ''}
              placeholder={searchPlaceholder}
              onChange={(e) => onQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && query) {
                  e.preventDefault();
                  onQuery('');
                }
              }}
            />
          </label>
        ) : null}
      </div>

      {/*
        ink-soft rather than the eyebrow's own ink-faint, which measures 2.54:1
        in the light theme. This line is the only place the match count appears,
        so it is information rather than decoration.

        The visible half appears only while something is narrowing the list: a
        table permanently announcing its own length is noise. The live region is
        unconditional, so the announcement never depends on chrome being shown.
      */}
      <p className="eyebrow mt-2 text-ink-soft">
        <span aria-hidden="true">{narrowed ? summary : null}</span>
        <span className="sr-only" role="status" aria-live="polite">
          {announced}
        </span>
      </p>
    </div>
  );
}
